"use client"

import { useQuery, useQueryClient } from "@tanstack/react-query"
import {
  confirmShopifyPlanCreditsAction,
  type ShopifyPlanCreditsResult,
} from "@/server-actions/shopify/plan-credits"

const queryKey = ["billing", "shopify-plan-credits"] as const
const CHECK_INTERVAL_MS = 3_000
/** About two minutes; past that the hourly poller credits the charge. */
const MAX_CHECKS = 40
/** Beyond a few in a row a failure is not transient; throwing also reaches the deploy-mismatch recovery. */
const MAX_FAILURES = 3

export type ShopifyPlanCreditsStatus = "checking" | "credited" | "none" | "delayed"

type PlanCreditsCheck = { credits: ShopifyPlanCreditsResult["credits"]; checks: number; failures: number }

/** Confirms a new Shopify plan's monthly credits, checking until Shopify lists the charge. */
export function useShopifyPlanCreditsStatus(enabled: boolean): ShopifyPlanCreditsStatus {
  const queryClient = useQueryClient()
  const { data, isError } = useQuery<PlanCreditsCheck>({
    queryKey,
    enabled,
    retry: false,
    gcTime: 0,
    queryFn: async () => {
      const previous = queryClient.getQueryData<PlanCreditsCheck>(queryKey)
      const checks = (previous?.checks ?? 0) + 1
      try {
        const result = await confirmShopifyPlanCreditsAction({})
        if ("status" in result) throw new Error(result.errorMessage)
        return { credits: result.credits, checks, failures: 0 }
      } catch (error) {
        const failures = (previous?.failures ?? 0) + 1
        if (failures >= MAX_FAILURES) throw error
        return { credits: "waiting", checks, failures }
      }
    },
    refetchInterval: query =>
      query.state.status !== "error" && isChecking(query.state.data) ? CHECK_INTERVAL_MS : false,
  })
  if (isError) return "delayed"
  switch (data?.credits) {
    case undefined:
      return "checking"
    case "credited":
      return "credited"
    case "none":
      return "none"
    case "later":
      return "delayed"
    case "waiting":
      return isChecking(data) ? "checking" : "delayed"
  }
}

function isChecking(check: PlanCreditsCheck | undefined): boolean {
  return !check || (check.credits === "waiting" && check.checks < MAX_CHECKS)
}
